import React from 'react';
import { PermissionCard } from '../atoms/PermissionCard';
import { CategoryHeader } from '../atoms/CategoryHeader';
import { Permission } from '@/services/rolesPermissionsService';
import { getPermissionDisplayName, getPermissionDescription } from '@/auth/permissions';

interface PermissionsListProps {
  permissions: Permission[];
  isLoading: boolean;
  searchText: string;
  // Actions
  onEditPermission: (permission: Permission) => void;
  onManageRoles: (permission: Permission) => void;
}

export const PermissionsList: React.FC<PermissionsListProps> = ({
  permissions,
  isLoading,
  searchText,
  onEditPermission,
  onManageRoles
}) => {
  // Filtrar permisos basado en el texto de búsqueda
  const filteredPermissions = permissions.filter(permission => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    const displayName = (permission.display_name || getPermissionDisplayName(permission.name)).toLowerCase();
    const description = (permission.description || getPermissionDescription(permission.name) || '').toLowerCase();
    const category = (permission.category || '').toLowerCase();
    const name = permission.name.toLowerCase();
    
    return displayName.includes(searchLower) || 
           description.includes(searchLower) || 
           category.includes(searchLower) ||
           name.includes(searchLower);
  });

  // Agrupar permisos por categoría
  const groupedPermissions = filteredPermissions.reduce((groups, permission) => {
    const category = permission.category || 'general';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  // Ordenar categorías alfabéticamente
  const sortedCategories = Object.keys(groupedPermissions).sort((a, b) => {
    // Poner 'general' al final
    if (a === 'general' && b !== 'general') return 1;
    if (b === 'general' && a !== 'general') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-xl font-semibold">Permisos del Sistema</h2>
        <div className="text-sm text-muted-foreground">
          Los permisos son gestionados por el sistema
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-muted-foreground">Cargando permisos...</p>
          </div>
        </div>
      ) : filteredPermissions.length === 0 && searchText ? (
        <div className="text-center text-muted-foreground py-8">
          No se encontraron permisos que coincidan con la búsqueda "{searchText}"
        </div>
      ) : sortedCategories.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          No hay permisos disponibles
        </div>
      ) : (
        <div className="space-y-8">
          {sortedCategories.map(category => (
            <div key={category}>
              <CategoryHeader 
                category={category} 
                count={groupedPermissions[category].length} 
              />
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {groupedPermissions[category]
                  .sort((a, b) => {
                    const aName = a.display_name || getPermissionDisplayName(a.name);
                    const bName = b.display_name || getPermissionDisplayName(b.name);
                    return aName.localeCompare(bName);
                  })
                  .map((permission) => (
                    <PermissionCard
                      key={permission.id}
                      permission={permission}
                      onEdit={onEditPermission}
                      onManageRoles={onManageRoles}
                    />
                  ))
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};